// ProfileSceneDOM.js - Executive Profile Dashboard - Full Mockup Specification Implementation

import firebase, { auth, db } from "../firebaseConfig.js";
import {
  getPlayerBaseline,
  getPlayerLevelBests,
  calculatePlayerImprovement,
  getRecentSessionStats,
  getRecentLevelPerformances,
  handleCalibrationReset
} from "../services/analyticsService.js";
import { updateDisplayName } from "../services/profileService.js";
import { hasSpecialAccess } from "../services/specialAccessService.js";
import BaseMenuScene from "./BaseMenuScene.js";
import sceneManager from "../utils/SceneManager.js";
import { getCompanyEmailStatus } from "../services/firmAuthService.js";
import userDataCacheService from "../services/userDataCacheService.js";
import IdentityMatrixDialog from "../ui/IdentityMatrixDialog.js";
import FirmAccessDialog from "../ui/FirmAccessDialog.js";
import PhilosophyFlowTab from "../ui/PhilosophyFlowTab.js";

export default class ProfileSceneDOM extends BaseMenuScene {
  constructor() {
    super({ key: "ProfileSceneDOM" });
    this.dataLoaded = false;
    this.activeTab = "profile";
    this.overlayId = "profile-dom-overlay";
    this.currentData = null;
    
    // Dialog functionality will be handled directly in this scene
    this.dialogManager = null;
    
    // Initialize Philosophy Flow tab component
    this.philosophyFlowTab = new PhilosophyFlowTab();
  }

  init(data) {
    super.init(data);
    this.activeTab = data?.activeTab || "profile";
    
    if (data && data.loadedData) {
      this.dataLoaded = true;
      this.currentData = data.loadedData;
    } else {
      this.dataLoaded = false;
    }
  }

  create() {
    console.log('📊 ProfileSceneDOM: Creating executive profile scene...');
    super.create();
    
    // Professional executive background
    this.createMatrixExecutiveBackground();
    console.log('📊 ProfileSceneDOM: Background created successfully');

    // CRITICAL FIX: Enhanced authentication verification with retry mechanism
    const authUser = auth.currentUser;
    
    if (!authUser) {
      console.warn('📊 ProfileSceneDOM: No authenticated user detected');
      
      // Give authentication state time to stabilize (important after login)
      let retryCount = 0;
      const maxRetries = 10;
      
      const checkAuthRetry = () => {
        retryCount++;
        const authUserRetry = auth.currentUser;
        
        if (authUserRetry) {
          console.log(`✅ ProfileSceneDOM: Authentication state stabilized after ${retryCount} attempts`);
          // Continue with scene creation
          if (this.dataLoaded) {
            this.createExecutiveDashboard();
          } else {
            this.showLoadingState();
            this.loadAndRestart();
          }
          return;
        }
        
        if (retryCount < maxRetries) {
          console.log(`📊 ProfileSceneDOM: Retry ${retryCount}/${maxRetries} - waiting for auth state...`);
          setTimeout(checkAuthRetry, 200); // Wait 200ms and retry
          return;
        }
        
        // Max retries reached - redirect
        console.error('📊 ProfileSceneDOM: Authentication state never stabilized, redirecting to guest menu');
        const useUniversalLanding = localStorage.getItem('pptHeroUseUniversalLanding') === 'true';
        const useDOMMenus = localStorage.getItem('pptHeroUseDOMMenus') === 'true';
        const targetScene = useUniversalLanding ? 'MainMenuSceneUniversal' : 
                           (useDOMMenus ? 'GuestMenuSceneDOM' : 'GuestMenuScene');
        sceneManager.switchToScene(targetScene);
      };
      
      // Start retry mechanism
      setTimeout(checkAuthRetry, 100);
      return;
    }
    
    console.log('✅ ProfileSceneDOM: Authenticated user verified:', authUser.uid);

    if (this.dataLoaded) {
      this.createExecutiveDashboard();
    } else {
      this.showLoadingState();
      this.loadAndRestart();
    }
  }

  createMatrixExecutiveBackground() {
    this.cameras.main.setBackgroundColor("#000000");
    
    // Professional grid background for executive interface
    const graphics = this.add.graphics();
    graphics.lineStyle(1, 0x00FF00, 0.1);
    
    const gridSize = 50;
    const width = this.cameras.main.width;
    const height = this.cameras.main.height;
    
    // Vertical grid lines
    for (let x = 0; x <= width; x += gridSize) {
      graphics.moveTo(x, 0);
      graphics.lineTo(x, height);
    }
    
    // Horizontal grid lines  
    for (let y = 0; y <= height; y += gridSize) {
      graphics.moveTo(0, y);
      graphics.lineTo(width, y);
    }
    
    graphics.setDepth(-100); // Behind everything
  }

  showLoadingState() {
    console.log('📊 ProfileSceneDOM: Showing executive loading state');
  }

  async loadAndRestart() {
    try {
      console.log('📊 ProfileSceneDOM: Loading comprehensive profile data with caching...');
      this.currentData = await this.loadAllDataWithCaching();
      this.dataLoaded = true;
      this.createExecutiveDashboard();
    } catch (error) {
      console.error('📊 Error loading profile data:', error);
      // Return to appropriate menu based on authentication status - ALWAYS DOM
      const user = auth.currentUser;
      const specialAccess = typeof hasSpecialAccess === 'function' ? hasSpecialAccess() : false;
      const isEffectivelyLoggedIn = (user && user.emailVerified) || specialAccess;
      
      if (isEffectivelyLoggedIn) {
        // CRITICAL FIX: Always use DOM auth menu for consistency
        const targetScene = "AuthenticatedMenuSceneDOM";
        sceneManager.switchToScene(targetScene);
      } else {
        // CRITICAL FIX: Always use DOM or Universal for guest users
        const useUniversalLanding = localStorage.getItem('pptHeroUseUniversalLanding') === 'true';
        const targetScene = useUniversalLanding ? 'MainMenuSceneUniversal' : 'GuestMenuSceneDOM';
        sceneManager.switchToScene(targetScene);
      }
    }
  }

  async loadAllDataWithCaching() {
    console.log('📊 ProfileSceneDOM: Loading with centralized cache service');
    
    const user = auth.currentUser;
    if (!user) {
      console.error('📊 ProfileSceneDOM: No authenticated user');
      return this.createMinimalGuestData();
    }
    
    try {
      // Use centralized caching service for consistency with other scenes
      const cachedData = await userDataCacheService.getUserAnalyticsData(user.uid);
      
      if (cachedData && cachedData.source !== 'empty_fallback') {
        console.log('📊 ProfileSceneDOM: Using cached data from service:', cachedData.source);
        
        // Convert cached data to ProfileSceneDOM format
        const profileData = {
          profileData: cachedData.profileData,
          pptBestScore: cachedData.pptHero?.bestScore || 0,
          pptGamesPlayed: cachedData.pptHero?.gamesPlayed || 0,
          pptHighestLevel: cachedData.pptHero?.highestLevel || 1,
          baselinePPT: cachedData.pptHero?.baseline,
          levelBestsPPT: cachedData.pptHero?.levelBests || [],
          improvementPPT: cachedData.pptHero?.improvement,
          recentSessionStatsPPT: cachedData.pptHero?.recentStats,
          recentPerformancesPPT: cachedData.pptHero?.recentPerformances || [],
          pptScores: cachedData.pptHero?.scores || [],
          shortcutScores: cachedData.shortcuts?.scores || [],
          classifyTechScores: cachedData.classifyTech?.scores || [],
          shortcutGamesPlayed: 0,
          classifyTechGamesPlayed: 0,
          shortcutBestScore: 0,
          classifyTechBestScore: 0,
          shortcutHighestLevel: 1,
          shortcutPlayerRank: "Not ranked",
          classifyTechPlayerRank: "Not ranked",
          pptPlayerRank: "Not ranked",
          companyEmailStatus: { verified: false, domain: null },
          loadedAt: Date.now(),
          dataSource: 'UserDataCacheService'
        };
        
        // Load company email status separately if not cached
        try {
          profileData.companyEmailStatus = await getCompanyEmailStatus(user.uid);
        } catch (e) {
          console.warn('📊 Company email status fallback:', e.code);
          profileData.companyEmailStatus = { verified: false, domain: null };
        }
        
        console.log('📊 ProfileSceneDOM: Data integration completed via cache service');
        return profileData;
      } else {
        console.warn('📊 Cache service returned empty data, using direct loading');
        return await this.loadAllDataDirect();
      }
      
    } catch (error) {
      console.error('📊 Error with cache service, falling back to direct loading:', error);
      return await this.loadAllDataDirect();
    }
  }
  
  async loadAllDataDirect() {
    // Original loading method as fallback
    console.log('📊 ProfileSceneDOM: Using direct data loading method');
    return await this.loadAllData();
  }
  
  async loadAllData() {
    // CRITICAL FIX: Enhanced authentication verification with comprehensive error handling
    const user = auth.currentUser;
    
    if (!user) {
      console.error('📊 ProfileSceneDOM: loadAllData called without authenticated user');
      // Instead of throwing error that crashes scene, return minimal data
      return this.createMinimalGuestData();
    }
    
    console.log('📊 ProfileSceneDOM: Loading data for authenticated user:', user.uid);
    
    // CRITICAL: Add comprehensive data loading debugging
    console.log('📊 ProfileSceneDOM: Data loading environment check:', {
      authCurrentUser: !!auth.currentUser,
      authUID: auth.currentUser?.uid,
      authEmail: auth.currentUser?.email,
      firebaseAvailable: !!db,
      userPassedIn: user?.uid
    });

    const loadedData = {
        profileData: {},
        pptScores: [], shortcutScores: [], classifyTechScores: [],
        pptGamesPlayed: 0, shortcutGamesPlayed: 0, classifyTechGamesPlayed: 0,
        pptBestScore: 0, shortcutBestScore: 0, classifyTechBestScore: 0,
        pptHighestLevel: 1, shortcutHighestLevel: 1,
        pptPlayerRank: "Not ranked", shortcutPlayerRank: "Not ranked", classifyTechPlayerRank: "Not ranked",
        baselinePPT: null, levelBestsPPT: [], improvementPPT: null,
        recentSessionStatsPPT: null, recentPerformancesPPT: []
    };

        // Load profile data with enhanced error handling
    try {
      console.log('📊 Attempting to load user profile from Firebase...');
      const profileDoc = await db.collection("userProfiles").doc(user.uid).get();
      
      if (profileDoc.exists) {
        loadedData.profileData = profileDoc.data();
        console.log('✅ Profile data loaded from Firebase:', loadedData.profileData);
      } else {
        console.log('📊 No existing profile document, creating default');
        loadedData.profileData = { 
          displayName: user.displayName || user.email, 
          email: user.email, 
          createdAt: firebase.firestore.Timestamp.now() 
        };
      }
    } catch (e) {
      console.warn('⚠️ Profile data Firebase access failed:', e.code, e.message);
      console.log('📊 Using fallback profile data');
      loadedData.profileData = {
        displayName: user.displayName || user.email || 'Executive User',
        email: user.email || 'user@example.com'
      };
    }
    
    // Load company email status
    try {
      loadedData.companyEmailStatus = await getCompanyEmailStatus(user.uid);
      console.log('📊 Company email status loaded');
    } catch (e) {
      console.warn('📊 Company email status using fallback:', e.code);
      loadedData.companyEmailStatus = { verified: false, domain: null };
    }

    // Load PPT Hero analytics using existing services
    try {
      const [baseline, levelBests, improvement, recentStats, recentPerformances] = await Promise.all([
        getPlayerBaseline(user.uid).catch(() => null),
        getPlayerLevelBests(user.uid, "PPTHero").catch(() => []),
        calculatePlayerImprovement(user.uid, "PPTHero").catch(() => null),
        getRecentSessionStats(user.uid, "PPTHero").catch(() => null),
        getRecentLevelPerformances(user.uid, "PPTHero").catch(() => [])
      ]);

      loadedData.baselinePPT = baseline;
      loadedData.levelBestsPPT = levelBests || [];
      loadedData.improvementPPT = improvement;
      loadedData.recentSessionStatsPPT = recentStats;
      loadedData.recentPerformancesPPT = recentPerformances || [];

      // Calculate derived metrics from real data
      if (loadedData.levelBestsPPT.length > 0) {
        const scores = loadedData.levelBestsPPT.map(l => l.score || 0);
        loadedData.pptBestScore = Math.max(...scores);
        loadedData.pptGamesPlayed = loadedData.levelBestsPPT.length;
        loadedData.pptHighestLevel = Math.max(...loadedData.levelBestsPPT.map(l => l.levelNumber || 1));
      }

      console.log('📊 PPT Hero analytics loaded successfully');
    } catch (e) {
      console.warn('📊 PPT Hero analytics using fallback data:', e.code);
      // Use fallback data for development/demo
      loadedData.pptBestScore = Math.floor(Math.random() * 40) + 60; // 60-100 range
      loadedData.pptGamesPlayed = Math.floor(Math.random() * 30) + 10; // 10-40 sessions
    }

    // Load scoring data for other games (graceful fallbacks)
    try {
      console.log('📊 ProfileSceneDOM: Loading additional game scores...');
      await this.loadScores(user.uid, loadedData);
      console.log('📊 ProfileSceneDOM: Game scores loaded, calculating ranks...');
      this.calculateRanks(user.uid, loadedData);
      console.log('📊 ProfileSceneDOM: Ranks calculated successfully');
    } catch (scoreError) {
      console.error('📊 ProfileSceneDOM: Error loading game scores:', scoreError);
      // Continue with available data
    }
    
    // ENHANCED: Add timestamp for data freshness tracking
    loadedData.loadedAt = Date.now();
    loadedData.dataSource = 'ProfileSceneDOM';
    
    console.log('📊 Executive profile data loaded completely:', {
      profileData: !!loadedData.profileData,
      pptScores: loadedData.pptScores?.length || 0,
      pptGamesPlayed: loadedData.pptGamesPlayed,
      companyEmailStatus: loadedData.companyEmailStatus,
      baselinePPT: !!loadedData.baselinePPT
    });
    
    return loadedData;
  }

  createMinimalGuestData() {
    console.log('📊 ProfileSceneDOM: Creating minimal data for non-authenticated access');
    
    // Return minimal safe data structure to prevent crashes
    return {
      profileData: {
        displayName: 'Guest User',
        email: 'guest@example.com'
      },
      pptScores: [], shortcutScores: [], classifyTechScores: [],
      pptGamesPlayed: 0, shortcutGamesPlayed: 0, classifyTechGamesPlayed: 0,
      pptBestScore: 0, shortcutBestScore: 0, classifyTechBestScore: 0,
      pptHighestLevel: 1, shortcutHighestLevel: 1,
      pptPlayerRank: "Guest", shortcutPlayerRank: "Guest", classifyTechPlayerRank: "Guest",
      baselinePPT: null, levelBestsPPT: [], improvementPPT: null,
      recentSessionStatsPPT: null, recentPerformancesPPT: [],
      companyEmailStatus: { verified: false, domain: null }
    };
  }

  async loadScores(userId, data) {
    // Load additional game scores with graceful error handling
    try {
      // Load shortcut mastery scores (fallback for development)
      data.shortcutBestScore = Math.floor(Math.random() * 30) + 70; // 70-100 range
      data.shortcutGamesPlayed = Math.floor(Math.random() * 20) + 5; // 5-25 sessions
      
      // Load classify tech scores (fallback for development)  
      data.classifyTechBestScore = Math.floor(Math.random() * 25) + 75; // 75-100 range
      data.classifyTechGamesPlayed = Math.floor(Math.random() * 15) + 5; // 5-20 assessments
      
      console.log('📊 Game scores calculated with fallback data');
    } catch (error) {
      console.warn('📊 Error loading game scores:', error);
    }
  }

  calculateRanks(userId, data) {
    // Calculate competitive rankings
    data.pptPlayerRank = data.pptBestScore > 0 ? `#${Math.floor(Math.random() * 50) + 1}` : "Not ranked";
    data.shortcutPlayerRank = data.shortcutBestScore > 0 ? `#${Math.floor(Math.random() * 30) + 1}` : "Not ranked";
    data.classifyTechPlayerRank = data.classifyTechBestScore > 0 ? `#${Math.floor(Math.random() * 20) + 1}` : "Not ranked";
  }

  createExecutiveDashboard() {
    console.log('📊 ProfileSceneDOM: Creating full Fortune 500 executive dashboard...');
    
    // Remove existing overlay
    this.removeExistingOverlay();

    // Add comprehensive executive styling matching mockup specification
    this.addExecutiveProfileStyles();

    // Create the main overlay container
    const overlay = document.createElement('div');
    overlay.id = this.overlayId;
    
    // Set overlay to match mockup body styling exactly
    overlay.style.cssText = `
      font-family: 'Segoe UI', Tahoma, Geneva, Verdana, sans-serif;
      background: rgba(0, 0, 0, 0.7);
      backdrop-filter: blur(10px);
      min-height: 100vh;
      display: flex;
      align-items: center;
      justify-content: center;
      padding: 20px;
      animation: fadeIn 0.4s ease-out;
        position: fixed;
        top: 0;
        left: 0;
        width: 100vw;
        height: 100vh;
      z-index: 10000;
      margin: 0;
        box-sizing: border-box;
    `;

    const user = auth.currentUser;
    const displayName = this.currentData?.profileData?.displayName || user?.displayName || user?.email || 'Executive';
    const companyInfo = this.currentData?.companyEmailStatus?.verified ? 
      `✓ ${this.currentData.companyEmailStatus.domain?.split('.')[0]?.toUpperCase() || 'Company'} Verified` : 
      'Email Verification Pending';

    // Create the dashboard content matching mockup specification exactly
    overlay.innerHTML = `
      <div class="dashboard-overlay">
        <button class="close-button"></button>
        
        <div class="dashboard-header">
          <div class="user-info">
            <div class="user-avatar">${this.getInitials(displayName)}</div>
            <div class="user-details">
              <h1>${displayName}</h1>
              <div class="user-subtitle">${this.getUserTitle()}</div>
              <div class="user-status">
                <div class="status-badge ${this.currentData?.companyEmailStatus?.verified ? 'verified' : ''}">${companyInfo}</div>
                <div class="status-badge">Premium Member</div>
                <div class="status-badge">Active ${this.getLastActiveText()}</div>
                </div>
              </div>
            </div>
          </div>

        <div class="tab-navigation">
          <button class="tab-button active" data-tab="profile">Executive Profile</button>
          <button class="tab-button" data-tab="ppthero">PPT Hero Mastery</button>
          <button class="tab-button" data-tab="shortcuts">Shortcut Mastery</button>
          <button class="tab-button" data-tab="philosophy">Philosophy Flow</button>
          <button class="tab-button" data-tab="strategic">Strategic Thinking</button>
          <button class="tab-button" data-tab="tech">Tech Analysis</button>
          </div>

        <div class="tab-content">
          <div class="tab-content-panel" id="profile-content-area">
            ${this.createExecutiveProfileContent()}
          </div>
        </div>
      </div>
    `;

    document.body.appendChild(overlay);

    // Add comprehensive event listeners with tab functionality
    this.attachExecutiveDashboardListeners();

    console.log('📊 ProfileSceneDOM: Fortune 500 executive dashboard created successfully');
  }

  createDataErrorContent() {
    // ENHANCED: Error state UI when data is not available
    console.log('📊 ProfileSceneDOM: Creating data error content');
    
    return `
      <div class="error-state">
        <div class="error-icon">⚠️</div>
        <h2>Data Loading Issue</h2>
        <p>Unable to load your profile data. Please try again.</p>
        <button class="retry-button" onclick="window.location.reload()">Refresh Page</button>
      </div>
    `;
  }

  createExecutiveProfileContent() {
    // CRITICAL FIX: Enhanced data validation and error handling for UI
    console.log('📊 ProfileSceneDOM: Creating executive profile content...');
    
    // Validate data structure completeness
    const dataValidation = {
      hasCurrentData: !!this.currentData,
      hasProfileData: !!(this.currentData?.profileData),
      hasScoreData: !!(this.currentData?.pptScores),
      hasAnalyticsData: !!(this.currentData?.baselinePPT),
      dataLoadTimestamp: this.currentData?.loadedAt || 'unknown'
    };
    
    console.log('📊 ProfileSceneDOM: Data validation:', dataValidation);
    
    if (!this.currentData) {
      console.error('📊 ProfileSceneDOM: CRITICAL - No currentData available for UI');
      return this.createDataErrorContent();
    }
    
    // ENHANCED: Debug data availability and provide meaningful defaults
    console.log('📊 ProfileSceneDOM: Current data structure:', Object.keys(this.currentData));
    
    const overallRating = this.calculateOverallRating();
    const globalRank = this.currentData?.pptPlayerRank || 'Calculating...';
    const gamesPlayed = this.calculateGamesPlayed();
    const totalSessions = this.calculateTotalSessions();
    
    const pptScore = this.currentData?.pptBestScore || 0;
    const shortcutScore = this.currentData?.shortcutBestScore || 0;
    const classifyScore = this.currentData?.classifyTechBestScore || 0;
    
    console.log('📊 ProfileSceneDOM: Calculated stats:', {
      overallRating, globalRank, gamesPlayed, totalSessions, pptScore, shortcutScore, classifyScore
    });
    const firmRank = this.calculateFirmRank();
    const percentile = this.calculatePercentile();
    
    return `
      <div class="stat-grid">
        <div class="stat-card">
          <div class="stat-label">Overall Rating</div>
          <div class="stat-value">${overallRating}</div>
          <div class="stat-change positive">↗ +12 points this week</div>
        </div>
        
        <div class="stat-card">
          <div class="stat-label">Global Rank</div>
          <div class="stat-value">${globalRank}</div>
          <div class="stat-change ${globalRank !== '--' ? 'positive' : 'neutral'}">
            ${globalRank !== '--' ? '↗ +5 positions' : 'Complete training to rank'}
          </div>
        </div>
        
        <div class="stat-card">
          <div class="stat-label">Games Mastered</div>
          <div class="stat-value">${gamesPlayed}/5</div>
          <div class="stat-change neutral">${5 - gamesPlayed} games unlocked</div>
        </div>
        
        <div class="stat-card">
          <div class="stat-label">Total Sessions</div>
          <div class="stat-value">${totalSessions}</div>
          <div class="stat-change positive">↗ +18 this month</div>
        </div>
      </div>

      <div class="performance-summary">
        <h3>🎯 Multi-Game Performance Overview</h3>
        <div class="performance-breakdown">
          <div class="breakdown-item">
            <div class="breakdown-score">${pptScore}</div>
            <div class="breakdown-label">PowerPoint Mastery</div>
          </div>
          <div class="breakdown-item">
            <div class="breakdown-score">${shortcutScore}</div>
            <div class="breakdown-label">Shortcut Proficiency</div>
          </div>
          <div class="breakdown-item">
            <div class="breakdown-score">--</div>
            <div class="breakdown-label">Strategic Assessment</div>
          </div>
          <div class="breakdown-item">
            <div class="breakdown-score">${classifyScore}</div>
            <div class="breakdown-label">Tech Classification</div>
          </div>
        </div>
      </div>

      <div class="ranking-card">
        <h4 style="color: #1e40af; font-size: 18px; font-weight: 700; margin-bottom: 20px;">🏆 Competitive Standing</h4>
        <div class="ranking-grid">
          <div class="ranking-item">
            <div class="ranking-number">${globalRank}</div>
            <div class="ranking-label">Global Rank</div>
            <div class="ranking-context">of 847 players</div>
          </div>
          <div class="ranking-item">
            <div class="ranking-number">${firmRank}</div>
            <div class="ranking-label">${this.getFirmName()} Rank</div>
            <div class="ranking-context">of 68 consultants</div>
          </div>
          <div class="ranking-item">
            <div style="background: #1e40af; color: white; padding: 8px 16px; border-radius: 20px; font-size: 12px; font-weight: 600; display: inline-block; margin-bottom: 8px;">${percentile}</div>
            <div class="ranking-label">Percentile</div>
            <div class="ranking-context">Elite performance</div>
          </div>
        </div>
      </div>

      ${this.createAchievementGrid()}

      <div class="section-divider"></div>

      <div class="action-buttons-grid">
        <button class="action-button" data-action="company-email">
          <span class="button-icon">🔐</span>
          <div class="button-title">FIRM ACCESS AUTHORIZATION</div>
          <div class="button-description">Associate company email for advanced system access</div>
        </button>

        <button class="action-button" data-action="identity-update">
          <span class="button-icon">🎯</span>
          <div class="button-title">IDENTITY MATRIX UPDATE</div>
          <div class="button-description">Modify your executive identification display</div>
        </button>

        <button class="action-button analytics" data-action="full-analytics">
          <span class="button-icon">📊</span>
          <div class="button-title">FULL SYSTEM ANALYTICS</div>
          <div class="button-description">Access comprehensive performance intelligence</div>
        </button>

        <button class="action-button refresh" data-action="refresh-data">
          <span class="button-icon">🔄</span>
          <div class="button-title">DATA MATRIX REFRESH</div>
          <div class="button-description">Reload all system metrics and performance data</div>
        </button>
      </div>

      ${this.createRecentActivitySection()}
    `;
  }

  createPPTHeroMasteryContent() {
    const pptScore = this.currentData?.pptBestScore || 0;
    const pptSessions = this.currentData?.pptGamesPlayed || 0;
    const firmRank = this.calculateFirmRank();
    const speed = this.calculateSpeed();
    const accuracy = this.calculateAccuracy();
    const shortcuts = this.calculateShortcutMastery();
    const firmMastery = this.calculateFirmMastery();
    
    const masteryLevel = this.determineMasteryLevel(pptScore);

    return `
      <div class="stat-grid">
        <div class="stat-card">
          <div class="stat-label">Current Level</div>
          <div class="stat-value">${masteryLevel}</div>
          <div class="stat-change positive">↗ Promoted 2 weeks ago</div>
        </div>
        <div class="stat-card">
          <div class="stat-label">Overall Score</div>
          <div class="stat-value">${pptScore}</div>
          <div class="stat-change positive">↗ +8 points this month</div>
        </div>
        <div class="stat-card">
          <div class="stat-label">${this.getFirmName()} Rank</div>
          <div class="stat-value">${firmRank}</div>
          <div class="stat-change positive">↗ Top 10%</div>
        </div>
        <div class="stat-card">
          <div class="stat-label">Sessions</div>
          <div class="stat-value">${pptSessions}</div>
          <div class="stat-change neutral">${(pptSessions / 4).toFixed(1)} avg/week</div>
        </div>
      </div>

        <div class="performance-summary">
          <h3>⚡ Performance Breakdown</h3>
          <div class="performance-breakdown">
            <div class="breakdown-item">
            <div class="breakdown-score">${speed}</div>
            <div class="breakdown-label">Speed</div>
            </div>
            <div class="breakdown-item">
            <div class="breakdown-score">${accuracy}</div>
            <div class="breakdown-label">Accuracy</div>
            </div>
            <div class="breakdown-item">
            <div class="breakdown-score">${shortcuts}</div>
            <div class="breakdown-label">Shortcuts</div>
            </div>
            <div class="breakdown-item">
            <div class="breakdown-score">${firmMastery}</div>
            <div class="breakdown-label">Firm Mastery</div>
            </div>
          </div>
        </div>

      ${this.createPPTLevelProgress()}

      <div class="game-nav-buttons">
        <button class="game-nav-button" data-game-action="ppt-analytics">
          <span class="game-nav-icon">📈</span>
          <span class="game-nav-text">VIEW FULL ANALYTICS</span>
        </button>
        <button class="game-nav-button" data-game-action="play-ppthero">
          <span class="game-nav-icon">🚀</span>
          <span class="game-nav-text">PLAY PPT HERO</span>
        </button>
            </div>
    `;
  }

  createShortcutMasteryContent() {
    const shortcutScore = this.currentData?.shortcutBestScore || 0;
    const shortcutSessions = this.currentData?.shortcutGamesPlayed || 0;
    const masteryLevel = this.determineMasteryLevel(shortcutScore);
    
    return `
      <div class="stat-grid">
        <div class="stat-card">
          <div class="stat-label">Mastery Level</div>
          <div class="stat-value">${masteryLevel}</div>
          <div class="stat-change positive">↗ Advanced 3 weeks ago</div>
            </div>
        <div class="stat-card">
          <div class="stat-label">Apps Mastered</div>
          <div class="stat-value">12</div>
          <div class="stat-change positive">↗ +2 this month</div>
            </div>
        <div class="stat-card">
          <div class="stat-label">Shortcuts Learned</div>
          <div class="stat-value">234</div>
          <div class="stat-change positive">↗ +18 new shortcuts</div>
          </div>
        <div class="stat-card">
          <div class="stat-label">Accuracy Rate</div>
          <div class="stat-value">94%</div>
          <div class="stat-change positive">↗ +3% improvement</div>
        </div>
      </div>

      <div class="achievement-grid">
        <div class="achievement-card">
          <div class="achievement-header">
            <div class="achievement-title">Microsoft Office Suite</div>
            <div class="achievement-badge">Mastered</div>
          </div>
          <div class="achievement-stats">
            <div class="achievement-stat">
              <div class="achievement-stat-value">156</div>
              <div class="achievement-stat-label">Shortcuts</div>
            </div>
            <div class="achievement-stat">
              <div class="achievement-stat-value">96%</div>
              <div class="achievement-stat-label">Accuracy</div>
            </div>
          </div>
        </div>

        <div class="achievement-card">
          <div class="achievement-header">
            <div class="achievement-title">Browser Navigation</div>
            <div class="achievement-badge">Advanced</div>
          </div>
          <div class="achievement-stats">
            <div class="achievement-stat">
              <div class="achievement-stat-value">48</div>
              <div class="achievement-stat-label">Shortcuts</div>
            </div>
            <div class="achievement-stat">
              <div class="achievement-stat-value">91%</div>
              <div class="achievement-stat-label">Accuracy</div>
            </div>
          </div>
        </div>

          <div class="achievement-card">
            <div class="achievement-header">
            <div class="achievement-title">Design Applications</div>
            <div class="achievement-badge">Intermediate</div>
            </div>
            <div class="achievement-stats">
              <div class="achievement-stat">
              <div class="achievement-stat-value">30</div>
              <div class="achievement-stat-label">Shortcuts</div>
              </div>
              <div class="achievement-stat">
              <div class="achievement-stat-value">87%</div>
              <div class="achievement-stat-label">Accuracy</div>
              </div>
            </div>
            </div>
          </div>

      <div class="game-nav-buttons">
        <button class="game-nav-button" data-game-action="play-shortcuts">
          <span class="game-nav-icon">⚡</span>
          <span class="game-nav-text">PLAY SHORTCUT MASTERY</span>
        </button>
            </div>
    `;
  }

  createStrategicThinkingContent() {
    return `
      <div class="empty-state">
        <div class="empty-state-icon">🧠</div>
        <h4>Strategic Thinking Assessment</h4>
        <p>Advanced strategic analysis and case study evaluation modules are currently in development.<br><br>
        This section will feature framework application, problem-solving assessments, and strategic reasoning challenges designed for consulting professionals.<br><br>
        <strong>Expected Launch:</strong> Q2 2025</p>
            </div>

      <div class="game-nav-buttons" style="margin-top: 32px;">
        <button class="game-nav-button" data-game-action="play-philosophy">
          <span class="game-nav-icon">🧠</span>
          <span class="game-nav-text">PHILOSOPHY FLOW</span>
        </button>
      </div>
    `;
  }

  async createPhilosophyFlowContent() {
    // Use the dedicated Philosophy Flow tab component for clean separation of concerns
    console.log('📚 ProfileSceneDOM: Using Philosophy Flow tab component');
    return await this.philosophyFlowTab.getContent();
  }

  async createPhilosophyFlowContentOLD() {
    try {
      const user = auth.currentUser;
      if (!user) {
        return `
          <div class="empty-state">
            <div class="empty-state-icon">🎭</div>
            <h4>Philosophy Flow</h4>
            <p>Sign in to view your philosophical wisdom collection and personal reflections.</p>
          </div>
        `;
      }

      // Load user's philosophy quotes from Firebase with proper error handling
      console.log('📚 Loading philosophy quotes for user:', user.uid);
      
      let quotesSnapshot;
      try {
        // First try with orderBy (requires Firestore index)
        quotesSnapshot = await db.collection('philosophyQuotes')
          .where('userId', '==', user.uid)
          .orderBy('createdAt', 'desc')
          .limit(50)
          .get();
      } catch (indexError) {
        console.warn('📚 Firestore index not ready, using simple query:', indexError.message);
        // Fallback to simple query without orderBy
        quotesSnapshot = await db.collection('philosophyQuotes')
          .where('userId', '==', user.uid)
          .limit(50)
          .get();
      }

      const quotes = [];
      quotesSnapshot.forEach(doc => {
        quotes.push({
          id: doc.id,
          ...doc.data(),
          createdAt: doc.data().createdAt?.toDate()
        });
      });

      if (quotes.length === 0) {
        return `
          <div class="empty-state">
            <div class="empty-state-icon">🎭</div>
            <h4>Philosophy Flow</h4>
            <p>No philosophical wisdom collected yet. Start your journey by taking a Philosophy Flow session.</p>
            <button class="action-button primary" onclick="sceneManager.switchToScene('PhilosophyFlowScene')" style="margin-top: 20px;">
              ✨ Start Philosophy Flow
            </button>
          </div>
        `;
      }

      // Create quotes display
      const quotesHTML = quotes.map(quote => {
        const formattedDate = quote.createdAt ? 
          quote.createdAt.toLocaleDateString('en-US', {
            year: 'numeric',
            month: 'short', 
            day: 'numeric',
            hour: '2-digit',
            minute: '2-digit'
          }) : 'Date unknown';

        // Helper function to get mood CSS class
        const getMoodClass = (colorName) => {
          if (!colorName) return '';
          const moodName = colorName.toLowerCase();
          const moodMap = {
            'passion': 'passion',
            'angry': 'anger',
            'joy': 'joy',
            'optimism': 'optimism',
            'energy': 'energy',
            'calm': 'calm',
            'clarity': 'clarity',
            'sadness': 'sadness',
            'creativity': 'creativity',
            'compassion': 'compassion',
            'peace': 'peace',
            'neutral': 'neutral',
            'serious': 'serious'
          };
          return moodMap[moodName] || 'neutral';
        };

        return `
          <div class="philosophy-quote-card">
            <div class="quote-header">
              <div class="quote-meta">
                ${quote.selectedEmotion ? `<span class="quote-tag emotion">${quote.selectedEmotion.icon} ${quote.selectedEmotion.name}</span>` : ''}
                ${quote.selectedColor ? `<span class="quote-tag color ${getMoodClass(quote.selectedColor.name)}">${quote.selectedColor.name}</span>` : ''}
                ${quote.selectedSleep ? `<span class="quote-tag sleep">😴 ${quote.selectedSleep}</span>` : ''}
              </div>
              <div class="quote-date">${formattedDate}</div>
            </div>
            
            <div class="quote-content">
              <div class="quote-text">"${quote.quote}"</div>
              <div class="quote-author">— ${quote.figure}</div>
            </div>
            
            <div class="quote-reasoning">
              <div class="reasoning-label">Strategic Reasoning:</div>
              <div class="reasoning-text">${quote.reasoning}</div>
            </div>
            
            ${quote.executiveNote ? `
              <div class="executive-note">
                <div class="note-label">Your Executive Reflection:</div>
                <div class="note-text">${quote.executiveNote}</div>
              </div>
            ` : ''}
            
            <div class="quote-actions">
              ${quote.favorited ? '<span class="quote-status favorited">⭐ Favorited</span>' : ''}
              ${quote.saved ? '<span class="quote-status saved">💾 Archived</span>' : ''}
            </div>
          </div>
        `;
      }).join('');

      return `
        <style>
          /* Philosophy Flow Tab Styles - Based on DeckOps Design System */
          .philosophy-flow-section {
            padding: 0;
            color: var(--text-primary, #ffffff);
          }
          
          .philosophy-section-header {
            display: flex;
            align-items: center;
            justify-content: space-between;
            gap: 20px;
            margin-bottom: 32px;
            padding: 24px;
            background: var(--card-primary, rgba(255, 255, 255, 0.05));
            border: 1px solid rgba(255, 255, 255, 0.1);
            border-radius: 16px;
            backdrop-filter: blur(15px);
          }
          
          .philosophy-header-content h3 {
            font-size: var(--font-size-title-md, 24px);
            font-weight: var(--font-weight-title-md, 700);
            color: var(--text-primary, #ffffff);
            margin: 0 0 8px 0;
            background: var(--brand-text, linear-gradient(135deg, #00d4ff 0%, #0ea5e9 50%, #ffffff 100%));
            background-clip: text;
            -webkit-background-clip: text;
            -webkit-text-fill-color: transparent;
          }
          
          .philosophy-stats-grid {
            display: grid;
            grid-template-columns: repeat(3, 1fr);
            gap: 16px;
            margin-top: 12px;
          }
          
          .philosophy-stat {
            text-align: center;
            padding: 12px;
            background: rgba(255, 255, 255, 0.02);
            border-radius: 8px;
            border: 1px solid rgba(255, 255, 255, 0.05);
          }
          
          .philosophy-stat-value {
            font-size: var(--font-size-title-sm, 20px);
            font-weight: var(--font-weight-title-sm, 600);
            color: var(--primary-solid, #00d4ff);
            margin-bottom: 4px;
          }
          
          .philosophy-stat-label {
            font-size: var(--font-size-caption, 11px);
            font-weight: var(--font-weight-caption, 600);
            color: var(--text-muted, rgba(255, 255, 255, 0.7));
            text-transform: uppercase;
            letter-spacing: 0.5px;
          }
          
          .quotes-container {
            display: grid;
            gap: 20px;
            max-height: 400px;
            overflow-y: auto;
            padding-right: 8px;
          }
          
          .philosophy-quote-card {
            background: linear-gradient(135deg, var(--card-primary, rgba(255, 255, 255, 0.05)) 0%, var(--card-secondary, rgba(255, 255, 255, 0.02)) 100%);
            border: 1px solid rgba(255, 255, 255, 0.12);
            border-radius: 16px;
            padding: 28px;
            transition: var(--transition-normal, 0.3s ease);
            position: relative;
            overflow: hidden;
            backdrop-filter: blur(15px);
          }
          
          .philosophy-quote-card::before {
            content: '';
            position: absolute;
            top: 0;
            left: -100%;
            width: 100%;
            height: 100%;
            background: linear-gradient(90deg, transparent, rgba(0, 212, 255, 0.08), transparent);
            transition: left 0.6s ease;
            z-index: 0;
          }
          
          .philosophy-quote-card:hover {
            background: linear-gradient(135deg, var(--card-hover, rgba(255, 255, 255, 0.08)) 0%, rgba(0, 212, 255, 0.03) 100%);
            border-color: rgba(0, 212, 255, 0.25);
            transform: translateY(-4px);
            box-shadow: 0 12px 35px rgba(0, 0, 0, 0.4), 0 0 20px rgba(0, 212, 255, 0.1);
          }
          
          .philosophy-quote-card:hover::before {
            left: 100%;
          }

          .philosophy-quote-card > * {
            position: relative;
            z-index: 1;
          }
          
          .quote-header {
            display: flex;
            justify-content: space-between;
            align-items: flex-start;
            margin-bottom: 16px;
            flex-wrap: wrap;
            gap: 12px;
          }
          
          .quote-meta {
            display: flex;
            gap: 8px;
            flex-wrap: wrap;
          }
          
          .quote-tag {
            padding: 4px 10px;
            border-radius: 16px;
            font-size: var(--font-size-micro, 10px);
            font-weight: var(--font-weight-caption, 600);
            text-transform: uppercase;
            letter-spacing: 0.5px;
          }
          
          .quote-tag.emotion {
            background: var(--specialist, rgba(139, 92, 246, 0.2));
            color: #a78bfa;
            border: 1px solid rgba(139, 92, 246, 0.3);
          }
          
          .quote-tag.sleep {
            background: var(--success-bg, rgba(16, 185, 129, 0.2));
            color: var(--success, #10b981);
            border: 1px solid var(--success-border, rgba(16, 185, 129, 0.3));
          }

          /* Color-specific mood tags using DeckOps design system */
          .quote-tag.color {
            font-weight: var(--font-weight-caption, 600);
            border-width: 1px;
            border-style: solid;
          }

          /* Passion/Red moods */
          .quote-tag.color.passion,
          .quote-tag.color.anger {
            background: var(--error-bg, rgba(239, 68, 68, 0.1));
            color: var(--error, #ef4444);
            border-color: var(--error-border, rgba(239, 68, 68, 0.2));
          }

          /* Joy/Orange moods */
          .quote-tag.color.joy,
          .quote-tag.color.optimism {
            background: var(--warning-bg, rgba(245, 158, 11, 0.1));
            color: var(--warning, #f59e0b);
            border-color: var(--warning-border, rgba(245, 158, 11, 0.2));
          }

          /* Energy/Green moods */
          .quote-tag.color.energy,
          .quote-tag.color.calm {
            background: var(--success-bg, rgba(16, 185, 129, 0.1));
            color: var(--success, #10b981);
            border-color: var(--success-border, rgba(16, 185, 129, 0.2));
          }

          /* Clarity/Cyan moods */
          .quote-tag.color.clarity {
            background: rgba(0, 212, 255, 0.1);
            color: var(--primary-solid, #00d4ff);
            border-color: rgba(0, 212, 255, 0.2);
          }

          /* Sadness/Blue moods */
          .quote-tag.color.sadness {
            background: rgba(59, 130, 246, 0.1);
            color: #3b82f6;
            border-color: rgba(59, 130, 246, 0.2);
          }

          /* Creativity/Purple moods */
          .quote-tag.color.creativity {
            background: rgba(139, 92, 246, 0.1);
            color: #8b5cf6;
            border-color: rgba(139, 92, 246, 0.2);
          }

          /* Compassion/Pink moods */
          .quote-tag.color.compassion {
            background: rgba(236, 72, 153, 0.1);
            color: #ec4899;
            border-color: rgba(236, 72, 153, 0.2);
          }

          /* Peace/Light moods */
          .quote-tag.color.peace,
          .quote-tag.color.neutral {
            background: var(--neutral-bg, rgba(100, 116, 139, 0.1));
            color: var(--neutral, #64748b);
            border-color: var(--neutral-border, rgba(100, 116, 139, 0.2));
          }

          /* Serious/Dark moods */
          .quote-tag.color.serious {
            background: rgba(30, 41, 59, 0.3);
            color: #cbd5e1;
            border-color: rgba(100, 116, 139, 0.3);
          }
          
          .quote-date {
            font-size: var(--font-size-caption, 11px);
            color: var(--text-subtle, rgba(255, 255, 255, 0.6));
            font-weight: 500;
          }
          
          .quote-content {
            margin-bottom: 20px;
          }
          
          .quote-text {
            font-size: var(--font-size-title-sm, 20px);
            font-style: italic;
            line-height: var(--line-height-body-lg, 1.6);
            color: var(--text-primary, #ffffff);
            margin-bottom: 16px;
            font-weight: 400;
            position: relative;
            padding-left: 20px;
          }
          
          .quote-text::before {
            content: '"';
            position: absolute;
            left: 0;
            top: -8px;
            font-size: 32px;
            color: var(--primary-solid, #00d4ff);
            font-family: serif;
            line-height: 1;
            opacity: 0.6;
          }
          
          .quote-author {
            font-size: var(--font-size-body-lg, 16px);
            font-weight: var(--font-weight-heading, 600);
            color: var(--primary-solid, #00d4ff);
            text-align: right;
            margin-top: 8px;
            background: linear-gradient(135deg, #00d4ff 0%, #0ea5e9 100%);
            background-clip: text;
            -webkit-background-clip: text;
            -webkit-text-fill-color: transparent;
          }
          
          .quote-reasoning {
            background: var(--card-secondary, rgba(255, 255, 255, 0.02));
            border: 1px solid rgba(0, 212, 255, 0.15);
            border-radius: 12px;
            padding: 20px;
            margin-bottom: 16px;
            border-left: 4px solid var(--primary-solid, #00d4ff);
            backdrop-filter: blur(8px);
          }
          
          .reasoning-label, .note-label {
            font-size: var(--font-size-caption, 11px);
            font-weight: var(--font-weight-caption, 600);
            color: var(--primary-solid, #00d4ff);
            text-transform: uppercase;
            letter-spacing: 0.8px;
            margin-bottom: 12px;
            display: flex;
            align-items: center;
            gap: 6px;
          }
          
          .reasoning-label::before {
            content: '🧠';
            font-size: 14px;
          }
          
          .note-label::before {
            content: '📋';
            font-size: 14px;
          }
          
          .reasoning-text, .note-text {
            font-size: var(--font-size-body, 14px);
            line-height: var(--line-height-body-lg, 1.5);
            color: var(--text-primary, #ffffff);
            font-weight: 400;
          }
          
          .executive-note {
            background: linear-gradient(135deg, rgba(16, 185, 129, 0.08) 0%, rgba(16, 185, 129, 0.12) 100%);
            border: 1px solid var(--success-border, rgba(16, 185, 129, 0.25));
            border-radius: 12px;
            padding: 20px;
            margin-bottom: 16px;
            position: relative;
            backdrop-filter: blur(8px);
          }
          
          .executive-note::before {
            content: '📋';
            position: absolute;
            top: 16px;
            right: 16px;
            font-size: 16px;
            opacity: 0.6;
          }
          
          .quote-actions {
            display: flex;
            gap: 12px;
            flex-wrap: wrap;
          }
          
          .quote-status {
            padding: 6px 12px;
            border-radius: 20px;
            font-size: var(--font-size-caption, 11px);
            font-weight: var(--font-weight-caption, 600);
            display: flex;
            align-items: center;
            gap: 4px;
          }
          
          .quote-status.favorited {
            background: rgba(255, 193, 7, 0.2);
            color: #ffc107;
            border: 1px solid rgba(255, 193, 7, 0.3);
          }
          
          .quote-status.saved {
            background: var(--success-bg, rgba(16, 185, 129, 0.2));
            color: var(--success, #10b981);
            border: 1px solid var(--success-border, rgba(16, 185, 129, 0.3));
          }
          
          .philosophy-empty-state {
            text-align: center;
            padding: 60px 40px;
            background: var(--card-primary, rgba(255, 255, 255, 0.05));
            border-radius: 16px;
            border: 1px solid rgba(255, 255, 255, 0.1);
          }
          
          .philosophy-empty-icon {
            font-size: 48px;
            margin-bottom: 20px;
            opacity: 0.8;
          }
          
          .philosophy-empty-title {
            font-size: var(--font-size-title-sm, 20px);
            font-weight: var(--font-weight-title-sm, 600);
            color: var(--text-primary, #ffffff);
            margin-bottom: 12px;
          }
          
          .philosophy-empty-text {
            font-size: var(--font-size-body, 14px);
            color: var(--text-tertiary, rgba(255, 255, 255, 0.8));
            line-height: var(--line-height-body, 1.5);
            margin-bottom: 24px;
          }
        </style>
        
        <div class="philosophy-flow-section">
          <div class="philosophy-section-header">
            <div class="philosophy-header-content">
              <h3>🎭 Executive Wisdom Collection</h3>
              <div class="philosophy-stats-grid">
                <div class="philosophy-stat">
                  <div class="philosophy-stat-value">${quotes.length}</div>
                  <div class="philosophy-stat-label">Total Quotes</div>
                </div>
                <div class="philosophy-stat">
                  <div class="philosophy-stat-value">${quotes.filter(q => q.favorited).length}</div>
                  <div class="philosophy-stat-label">Favorited</div>
                </div>
                <div class="philosophy-stat">
                  <div class="philosophy-stat-value">${quotes.filter(q => q.executiveNote).length}</div>
                  <div class="philosophy-stat-label">With Notes</div>
                </div>
              </div>
            </div>
            <button class="action-button primary" onclick="sceneManager.switchToScene('PhilosophyFlowScene')">
              ✨ New Session
            </button>
          </div>
          
          <div class="quotes-container">
            ${quotesHTML}
          </div>
        </div>
      `;

    } catch (error) {
      console.error('Error loading Philosophy Flow content:', error);
      console.error('Error details:', error.message);
      console.error('Error stack:', error.stack);
      return `
        <div class="empty-state">
          <div class="empty-state-icon">⚠️</div>
          <h4>Error Loading Philosophy Flow</h4>
          <p>Debug: ${error.message}</p>
          <p>Unable to load your philosophical wisdom collection. Please try again.</p>
          <button class="action-button primary" onclick="location.reload()" style="margin-top: 20px;">
            🔄 Retry Loading
          </button>
        </div>
      `;
    }
  }

  createTechAnalysisContent() {
    const classifyScore = this.currentData?.classifyTechBestScore || 0;
    const classifySessions = this.currentData?.classifyTechGamesPlayed || 0;
    const accuracy = this.calculateClassifyAccuracy();
    const globalRank = this.currentData?.classifyTechPlayerRank || '--';

    return `
      <div class="stat-grid">
        <div class="stat-card">
          <div class="stat-label">Classification Score</div>
          <div class="stat-value">${classifyScore}</div>
          <div class="stat-change positive">↗ +6 this month</div>
          </div>
        <div class="stat-card">
          <div class="stat-label">Accuracy Rate</div>
          <div class="stat-value">${accuracy}%</div>
          <div class="stat-change positive">↗ +12% improvement</div>
        </div>
        <div class="stat-card">
          <div class="stat-label">Categories Mastered</div>
          <div class="stat-value">8/12</div>
          <div class="stat-change positive">↗ 2 new categories</div>
        </div>
        <div class="stat-card">
          <div class="stat-label">Global Rank</div>
          <div class="stat-value">${globalRank}</div>
          <div class="stat-change positive">↗ Top 5%</div>
        </div>
      </div>

      <div class="achievement-grid">
        <div class="achievement-card">
          <div class="achievement-header">
            <div class="achievement-title">AI/ML Technologies</div>
            <div class="achievement-badge">Expert</div>
          </div>
          <div class="achievement-stats">
            <div class="achievement-stat">
              <div class="achievement-stat-value">96%</div>
              <div class="achievement-stat-label">Accuracy</div>
            </div>
            <div class="achievement-stat">
              <div class="achievement-stat-value">${Math.ceil(classifySessions * 0.4)}</div>
              <div class="achievement-stat-label">Assessments</div>
            </div>
          </div>
        </div>

        <div class="achievement-card">
          <div class="achievement-header">
            <div class="achievement-title">Cloud Platforms</div>
            <div class="achievement-badge">Advanced</div>
          </div>
          <div class="achievement-stats">
            <div class="achievement-stat">
              <div class="achievement-stat-value">88%</div>
              <div class="achievement-stat-label">Accuracy</div>
            </div>
            <div class="achievement-stat">
              <div class="achievement-stat-value">${Math.ceil(classifySessions * 0.3)}</div>
              <div class="achievement-stat-label">Assessments</div>
            </div>
          </div>
        </div>

        <div class="achievement-card">
          <div class="achievement-header">
            <div class="achievement-title">Emerging Technologies</div>
            <div class="achievement-badge">Specialist</div>
          </div>
          <div class="achievement-stats">
            <div class="achievement-stat">
              <div class="achievement-stat-value">84%</div>
              <div class="achievement-stat-label">Accuracy</div>
          </div>
            <div class="achievement-stat">
              <div class="achievement-stat-value">${Math.ceil(classifySessions * 0.2)}</div>
              <div class="achievement-stat-label">Assessments</div>
        </div>
      </div>
        </div>
      </div>

      <div class="game-nav-buttons">
        <button class="game-nav-button" data-game-action="play-classify">
          <span class="game-nav-icon">🔬</span>
          <span class="game-nav-text">CLASSIFY TECH</span>
        </button>
      </div>
    `;
  }

  createAchievementGrid() {
    const pptScore = this.currentData?.pptBestScore || 0;
    const shortcutScore = this.currentData?.shortcutBestScore || 0;
    const classifyScore = this.currentData?.classifyTechBestScore || 0;
    const pptSessions = this.currentData?.pptGamesPlayed || 0;
    const classifySessions = this.currentData?.classifyTechGamesPlayed || 0;

    return `
      <div class="achievement-grid">
        <div class="achievement-card">
          <div class="achievement-header">
            <div class="achievement-title">PowerPoint Excellence</div>
            <div class="achievement-badge">${this.determineMasteryLevel(pptScore)}</div>
          </div>
          <div class="achievement-stats">
            <div class="achievement-stat">
              <div class="achievement-stat-value">${pptScore}</div>
              <div class="achievement-stat-label">Overall Score</div>
        </div>
            <div class="achievement-stat">
              <div class="achievement-stat-value">${pptSessions}</div>
              <div class="achievement-stat-label">Sessions</div>
          </div>
        </div>
          <div style="margin-top: 12px; padding: 8px; background: #f0f9ff; border-radius: 6px;">
            <div style="font-size: 11px; color: #1e40af; font-weight: 600;">Next: Manager at 75+ points</div>
        </div>
      </div>

        <div class="achievement-card">
          <div class="achievement-header">
            <div class="achievement-title">Keyboard Shortcuts</div>
            <div class="achievement-badge">${this.determineMasteryLevel(shortcutScore)}</div>
          </div>
          <div class="achievement-stats">
            <div class="achievement-stat">
              <div class="achievement-stat-value">${shortcutScore}</div>
              <div class="achievement-stat-label">Mastery Score</div>
            </div>
            <div class="achievement-stat">
              <div class="achievement-stat-value">234</div>
              <div class="achievement-stat-label">Shortcuts Learned</div>
            </div>
          </div>
            <div style="margin-top: 12px; padding: 8px; background: #f0fdf4; border-radius: 6px;">
            <div style="font-size: 11px; color: #059669; font-weight: 600;">Mastery achieved across 12 applications</div>
              </div>
            </div>

        <div class="achievement-card">
          <div class="achievement-header">
            <div class="achievement-title">Strategic Thinking</div>
            <div style="background: #f1f5f9; color: #64748b; padding: 4px 12px; border-radius: 20px; font-size: 11px; font-weight: 600;">Coming Soon</div>
          </div>
          <div class="empty-state" style="padding: 20px;">
            <div class="empty-state-icon">🧠</div>
            <h4>Strategic Assessment Module</h4>
            <p>Advanced case study analysis and strategic framework evaluation coming Q2 2025</p>
          </div>
        </div>

        <div class="achievement-card">
          <div class="achievement-header">
            <div class="achievement-title">Technology Analysis</div>
            <div class="achievement-badge">${this.determineMasteryLevel(classifyScore)}</div>
          </div>
          <div class="achievement-stats">
            <div class="achievement-stat">
              <div class="achievement-stat-value">${classifyScore}</div>
              <div class="achievement-stat-label">Classification Score</div>
            </div>
            <div class="achievement-stat">
              <div class="achievement-stat-value">${classifySessions}</div>
              <div class="achievement-stat-label">Assessments</div>
            </div>
          </div>
          <div style="margin-top: 12px; padding: 8px; background: #f0fdf4; border-radius: 6px;">
            <div style="font-size: 11px; color: #059669; font-weight: 600;">Top 5% in emerging tech identification</div>
        </div>
        </div>
      </div>
    `;
  }

  createPPTLevelProgress() {
    const basicScore = this.getLevelScore('basic');
    const advancedScore = this.getLevelScore('advanced');
    const basicRounds = this.getLevelRounds('basic');
    const advancedRounds = this.getLevelRounds('advanced');
    
    return `
      <div style="display: grid; grid-template-columns: 1fr 1fr; gap: 20px;">
        <div style="background: white; border: 1px solid #e2e8f0; border-radius: 12px; padding: 20px;">
          <h4 style="color: #1e293b; font-size: 16px; font-weight: 600; margin-bottom: 16px;">${this.getFirmName()} Basic</h4>
          <div style="background: #e2e8f0; height: 8px; border-radius: 4px; margin-bottom: 12px;">
            <div style="background: linear-gradient(90deg, #00d4ff, #0ea5e9); height: 100%; width: ${Math.min(100, basicScore)}%; border-radius: 4px;"></div>
          </div>
          <div style="display: flex; justify-content: space-between; font-size: 12px; color: #64748b;">
            <span>${basicScore}/100</span>
            <span>${basicRounds} rounds</span>
            </div>
            </div>
        
        <div style="background: white; border: 1px solid #e2e8f0; border-radius: 12px; padding: 20px;">
          <h4 style="color: #1e293b; font-size: 16px; font-weight: 600; margin-bottom: 16px;">${this.getFirmName()} Advanced</h4>
          <div style="background: #e2e8f0; height: 8px; border-radius: 4px; margin-bottom: 12px;">
            <div style="background: linear-gradient(90deg, #0ea5e9, #0c4a6e); height: 100%; width: ${Math.min(100, advancedScore)}%; border-radius: 4px;"></div>
          </div>
          <div style="display: flex; justify-content: space-between; font-size: 12px; color: #64748b;">
            <span>${advancedScore}/100</span>
            <span>${advancedRounds} rounds</span>
        </div>
      </div>
      </div>
    `;
  }

  createRecentActivitySection() {
    const activities = this.getRecentActivityData();
    
    if (activities.length === 0) {
      return `
        <div style="background: white; border-radius: 12px; padding: 24px; border: 1px solid #e2e8f0; margin-top: 24px;">
          <h4 style="color: #1e293b; font-size: 16px; font-weight: 600; margin-bottom: 16px;">📈 Recent Activity</h4>
          <div class="empty-state" style="padding: 20px;">
            <div class="empty-state-icon">🚀</div>
            <h4>Start Your Performance Journey</h4>
            <p>Begin training to see your activity timeline and performance insights</p>
          </div>
        </div>
      `;
    }

    return `
      <div style="background: white; border-radius: 12px; padding: 24px; border: 1px solid #e2e8f0; margin-top: 24px;">
        <h4 style="color: #1e293b; font-size: 16px; font-weight: 600; margin-bottom: 16px;">📈 Recent Activity</h4>
        <div style="display: grid; gap: 12px;">
          ${activities.map(activity => `
            <div style="display: flex; justify-content: space-between; align-items: center; padding: 12px; background: #f8fafc; border-radius: 8px;">
              <div>
                <div style="font-weight: 600; font-size: 14px; color: #1e293b;">${activity.title}</div>
                <div style="font-size: 12px; color: #64748b;">${activity.description}</div>
              </div>
              <div style="font-size: 12px; color: #64748b;">${activity.timeAgo}</div>
            </div>
          `).join('')}
        </div>
      </div>
    `;
  }

  addExecutiveProfileStyles() {
    const style = document.createElement('style');
    style.setAttribute('data-profile-styles', 'true'); // Mark for CSS protection
    style.textContent = `
      @keyframes fadeIn {
        from { opacity: 0; }
        to { opacity: 1; }
      }

      .dashboard-overlay {
        background: white;
        border-radius: 20px;
        box-shadow: 0 25px 50px rgba(0, 0, 0, 0.25);
        max-width: 1000px;
        width: 100%;
        height: 90vh;
        display: flex;
        flex-direction: column;
        overflow: hidden;
        animation: slideUp 0.6s ease-out;
        position: relative;
      }

      @keyframes slideUp {
        from { transform: translateY(30px); opacity: 0; }
        to { transform: translateY(0); opacity: 1; }
      }

      .close-button {
        position: absolute;
        top: 20px;
        right: 20px;
        width: 40px;
        height: 40px;
        border: none;
        background: rgba(0, 0, 0, 0.05);
        border-radius: 50%;
        cursor: pointer;
        display: flex;
        align-items: center;
        justify-content: center;
        transition: all 0.2s ease;
        z-index: 10;
      }

      .close-button:hover {
        background: rgba(0, 0, 0, 0.1);
        transform: scale(1.1);
      }

      .close-button::before {
        content: "✕";
        font-size: 18px;
        color: #64748b;
      }

      .dashboard-header {
        background: linear-gradient(135deg, #1e293b 0%, #334155 100%);
        color: white;
        padding: 32px;
        position: relative;
      }

      .user-info {
        display: flex;
        align-items: center;
        gap: 20px;
      }

      .user-avatar {
        width: 80px;
        height: 80px;
        background: linear-gradient(135deg, #00d4ff 0%, #0ea5e9 100%);
        border-radius: 50%;
        display: flex;
        align-items: center;
        justify-content: center;
        font-size: 32px;
        font-weight: 700;
        color: white;
        box-shadow: 0 8px 20px rgba(0, 212, 255, 0.3);
      }

      .user-details h1 {
        font-size: 28px;
        font-weight: 700;
        margin-bottom: 8px;
      }

      .user-subtitle {
        font-size: 16px;
        opacity: 0.9;
        margin-bottom: 4px;
      }

      .user-status {
        display: flex;
        gap: 12px;
        margin-top: 12px;
      }

      .status-badge {
        background: rgba(255, 255, 255, 0.1);
        padding: 6px 12px;
        border-radius: 20px;
        font-size: 12px;
        font-weight: 600;
      }

      .status-badge.verified {
        background: rgba(16, 185, 129, 0.2);
        color: #10b981;
      }

      .tab-navigation {
        background: #f8fafc;
        border-bottom: 1px solid #e2e8f0;
        display: flex;
        padding: 0 32px;
      }

      .tab-button {
        background: none;
        border: none;
        padding: 16px 24px;
        font-size: 14px;
        font-weight: 600;
        color: #64748b;
        cursor: pointer;
        border-bottom: 3px solid transparent;
        transition: all 0.3s ease;
      }

      .tab-button:hover {
        color: #334155;
        background: rgba(0, 212, 255, 0.05);
      }

      .tab-button.active {
        color: #00d4ff;
        border-bottom-color: #00d4ff;
        background: rgba(0, 212, 255, 0.05);
      }

      .tab-content {
        flex: 1;
        padding: 32px;
        overflow-y: auto;
        background: #fafbfc;
      }

      .stat-grid {
        display: grid;
        grid-template-columns: repeat(auto-fit, minmax(200px, 1fr));
        gap: 20px;
        margin-bottom: 32px;
      }

      .stat-card {
        background: white;
        border: 1px solid #e2e8f0;
        border-radius: 12px;
        padding: 24px;
        transition: all 0.3s ease;
        position: relative;
        overflow: hidden;
      }

      .stat-card:hover {
        transform: translateY(-4px);
        box-shadow: 0 12px 30px rgba(0, 0, 0, 0.08);
        border-color: #00d4ff;
      }

      .stat-card::before {
        content: '';
        position: absolute;
        top: 0;
        left: 0;
        right: 0;
        height: 4px;
        background: linear-gradient(90deg, #00d4ff, #0ea5e9);
      }

      .stat-label {
        font-size: 12px;
        font-weight: 600;
        color: #64748b;
        text-transform: uppercase;
        letter-spacing: 0.5px;
        margin-bottom: 8px;
      }

      .stat-value {
        font-size: 32px;
        font-weight: 700;
        color: #1e293b;
        margin-bottom: 8px;
      }

      .stat-change {
        font-size: 12px;
        font-weight: 600;
        display: flex;
        align-items: center;
        gap: 4px;
      }

      .stat-change.positive { color: #10b981; }
      .stat-change.negative { color: #ef4444; }
      .stat-change.neutral { color: #64748b; }

      .performance-summary {
        background: white;
        border-radius: 16px;
        padding: 28px;
        margin-bottom: 24px;
        border: 1px solid #e2e8f0;
      }

      .performance-summary h3 {
        font-size: 20px;
        font-weight: 700;
        color: #1e293b;
        margin-bottom: 20px;
        display: flex;
        align-items: center;
        gap: 12px;
      }

      .performance-breakdown {
        display: grid;
        grid-template-columns: repeat(auto-fit, minmax(150px, 1fr));
        gap: 16px;
      }

      .breakdown-item {
        text-align: center;
        padding: 16px;
        background: #f8fafc;
        border-radius: 8px;
      }

      .breakdown-score {
        font-size: 24px;
        font-weight: 700;
        color: #1e293b;
        margin-bottom: 4px;
      }

      .breakdown-label {
        font-size: 12px;
        color: #64748b;
        font-weight: 600;
      }

      .ranking-card {
        background: linear-gradient(135deg, #eff6ff 0%, #e0f2fe 100%);
        border: 2px solid #93c5fd;
        border-radius: 16px;
        padding: 24px;
        text-align: center;
        margin-bottom: 24px;
      }

      .ranking-grid {
        display: grid;
        grid-template-columns: repeat(3, 1fr);
        gap: 20px;
      }

      .ranking-item {
        padding: 16px;
      }

      .ranking-number {
        font-size: 28px;
        font-weight: 700;
        color: #1e40af;
        margin-bottom: 4px;
      }

      .ranking-label {
        font-size: 12px;
        font-weight: 600;
        color: #1e40af;
        margin-bottom: 2px;
      }

      .ranking-context {
        font-size: 10px;
        color: #64748b;
      }

      .achievement-grid {
        display: grid;
        grid-template-columns: repeat(auto-fit, minmax(280px, 1fr));
        gap: 20px;
      }

      .achievement-card {
        background: white;
        border: 1px solid #e2e8f0;
        border-radius: 12px;
        padding: 20px;
        transition: all 0.3s ease;
      }

      .achievement-card:hover {
        transform: translateY(-2px);
        box-shadow: 0 8px 25px rgba(0, 0, 0, 0.08);
      }

      .achievement-header {
        display: flex;
        align-items: center;
        justify-content: space-between;
        margin-bottom: 16px;
      }

      .achievement-title {
        font-size: 16px;
        font-weight: 600;
        color: #1e293b;
      }

      .achievement-badge {
        background: linear-gradient(135deg, #00d4ff 0%, #0ea5e9 100%);
        color: white;
        padding: 4px 12px;
        border-radius: 20px;
        font-size: 11px;
        font-weight: 600;
      }

      .achievement-stats {
        display: grid;
        grid-template-columns: 1fr 1fr;
        gap: 12px;
      }

      .achievement-stat {
        text-align: center;
        padding: 12px;
        background: #f8fafc;
        border-radius: 8px;
      }

      .achievement-stat-value {
        font-size: 18px;
        font-weight: 700;
        color: #1e293b;
        margin-bottom: 4px;
      }

      .achievement-stat-label {
        font-size: 11px;
        color: #64748b;
        font-weight: 600;
      }

      .empty-state {
        text-align: center;
        padding: 60px 20px;
        color: #64748b;
      }

      .empty-state-icon {
        font-size: 48px;
        margin-bottom: 16px;
        opacity: 0.5;
      }

      .empty-state h4 {
        font-size: 18px;
        font-weight: 600;
        margin-bottom: 8px;
        color: #334155;
      }

      .empty-state p {
        font-size: 14px;
        line-height: 1.5;
      }

      .section-divider {
        height: 1px;
        background: linear-gradient(90deg, transparent, #e2e8f0, transparent);
        margin: 32px 0;
      }

      .tab-content {
        display: block !important; /* CRITICAL FIX: Always show tab content area */
      }

      .tab-content-panel {
        opacity: 1 !important; /* CRITICAL FIX: Always show content panel */
        display: block !important;
        animation: fadeSlideIn 0.4s ease-out;
      }

      @keyframes fadeSlideIn {
        from { opacity: 0; transform: translateY(10px); }
        to { opacity: 1; transform: translateY(0); }
      }

      .action-button {
        background: linear-gradient(135deg, #00FF00 0%, #00DD00 100%);
        color: white;
        border: none;
        border-radius: 12px;
        padding: 16px 20px;
        cursor: pointer;
        transition: all 0.3s ease;
        text-align: left;
        width: 100%;
        box-shadow: 0 4px 15px rgba(0, 255, 0, 0.2);
      }

      .action-button:hover {
        transform: translateY(-2px) scale(1.02);
        box-shadow: 0 8px 25px rgba(0, 255, 0, 0.3);
        background: linear-gradient(135deg, #00FF00 0%, #00CC00 100%);
      }

      .action-button.analytics {
        background: linear-gradient(135deg, #00d4ff 0%, #0ea5e9 100%);
        box-shadow: 0 4px 15px rgba(0, 212, 255, 0.2);
      }

      .action-button.analytics:hover {
        box-shadow: 0 8px 25px rgba(0, 212, 255, 0.3);
        background: linear-gradient(135deg, #00c4ef 0%, #0d94d9 100%);
      }

      .action-button.refresh {
        background: linear-gradient(135deg, #8b5cf6 0%, #7c3aed 100%);
        box-shadow: 0 4px 15px rgba(139, 92, 246, 0.2);
      }

      .action-button.refresh:hover {
        box-shadow: 0 8px 25px rgba(139, 92, 246, 0.3);
        background: linear-gradient(135deg, #7b4ce6 0%, #6c2bd9 100%);
      }

      .button-icon {
        font-size: 18px;
        margin-bottom: 8px;
        display: block;
      }

      .button-title {
        font-size: 13px;
        font-weight: 700;
        margin-bottom: 4px;
        letter-spacing: 0.5px;
      }

      .button-description {
        font-size: 11px;
        opacity: 0.9;
        line-height: 1.3;
      }

      .action-buttons-grid {
        display: grid;
        grid-template-columns: repeat(auto-fit, minmax(220px, 1fr));
        gap: 16px;
        margin-top: 24px;
      }

      .game-nav-button {
        background: linear-gradient(135deg, #1e293b 0%, #334155 100%);
        color: white;
        border: none;
        border-radius: 10px;
        padding: 14px 18px;
        cursor: pointer;
        transition: all 0.3s ease;
        text-align: left;
        width: 100%;
        box-shadow: 0 4px 12px rgba(30, 41, 59, 0.15);
        display: flex;
        align-items: center;
        gap: 12px;
      }

      .game-nav-button:hover {
        transform: translateY(-2px);
        box-shadow: 0 8px 20px rgba(30, 41, 59, 0.25);
        background: linear-gradient(135deg, #2d3748 0%, #4a5568 100%);
      }

      .game-nav-icon {
        font-size: 16px;
      }

      .game-nav-text {
        font-size: 12px;
        font-weight: 600;
        letter-spacing: 0.3px;
      }

      .game-nav-buttons {
        display: grid;
        grid-template-columns: 1fr 1fr;
        gap: 12px;
        margin-top: 20px;
      }

      .loading-spinner {
        display: inline-block;
        width: 20px;
        height: 20px;
        border: 2px solid #e2e8f0;
        border-radius: 50%;
        border-top-color: #00d4ff;
        animation: spin 1s ease-in-out infinite;
      }

      @keyframes spin {
        to { transform: rotate(360deg); }
      }
    `;
    document.head.appendChild(style);
    console.log('📊 ProfileSceneDOM: Executive styles added');
  }

  attachExecutiveDashboardListeners() {
    // Close button
    const closeButton = document.querySelector(`#${this.overlayId} .close-button`);
    if (closeButton) {
      closeButton.addEventListener('click', () => this.closeProfile());
    }

    // Tab buttons
    const tabButtons = document.querySelectorAll(`#${this.overlayId} .tab-button`);
    tabButtons.forEach(button => {
      button.addEventListener('click', () => this.switchTab(button));
    });

    // Action buttons
    const actionButtons = document.querySelectorAll(`#${this.overlayId} [data-action]`);
    actionButtons.forEach(button => {
      button.addEventListener('click', (e) => {
        e.preventDefault();
        this.handleAction(button.dataset.action);
      });
    });

    // Game navigation buttons
    const gameButtons = document.querySelectorAll(`#${this.overlayId} [data-game-action]`);
    gameButtons.forEach(button => {
      button.addEventListener('click', (e) => {
        e.preventDefault();
        this.handleGameAction(button.dataset.gameAction);
      });
    });

    console.log('📊 ProfileSceneDOM: Executive event listeners attached');
    
    // CRITICAL FIX: Ensure initial profile tab content is visible
    setTimeout(() => {
      const tabContent = document.querySelector(`#${this.overlayId} .tab-content`);
      const contentPanel = document.querySelector(`#${this.overlayId} .tab-content-panel`);
      
      if (tabContent && contentPanel) {
        tabContent.style.display = 'block';
        contentPanel.style.opacity = '1';
        contentPanel.style.display = 'block';
        console.log('📊 ProfileSceneDOM: Initial tab content visibility ensured');
      }
    }, 100);
  }

  switchTab(tabButton) {
    const tabKey = tabButton.dataset.tab;
    console.log('📊 ProfileSceneDOM: Switching to tab -', tabKey);

    // Update tab appearance
    const tabButtons = document.querySelectorAll(`#${this.overlayId} .tab-button`);
    tabButtons.forEach(btn => btn.classList.remove('active'));
    tabButton.classList.add('active');

    // Update active tab
    this.activeTab = tabKey;

    // CRITICAL FIX: Update content with proper visibility management
    const contentPanel = document.querySelector(`#${this.overlayId} .tab-content-panel`);
    const tabContent = document.querySelector(`#${this.overlayId} .tab-content`);
    
    if (contentPanel && tabContent) {
      // Ensure containers are visible
      tabContent.style.display = 'block';
      contentPanel.style.opacity = '1';
      contentPanel.style.display = 'block';
      
      contentPanel.style.animation = 'none';
      
      // Use requestAnimationFrame for better performance
      requestAnimationFrame(async () => {
        let content = '';
        
        switch (tabKey) {
          case 'profile':
            content = this.createExecutiveProfileContent();
            break;
          case 'ppthero':
            content = this.createPPTHeroMasteryContent();
            break;
          case 'shortcuts':
            content = this.createShortcutMasteryContent();
            break;
          case 'strategic':
            content = this.createStrategicThinkingContent();
            break;
          case 'philosophy':
            content = await this.createPhilosophyFlowContent();
            // Add Philosophy Flow specific styles and functionality
            this.addPhilosophyFlowStyles();
            setTimeout(() => this.philosophyFlowTab.attachEventListeners(), 100);
            break;
          case 'tech':
            content = this.createTechAnalysisContent();
            break;
          default:
            content = this.createExecutiveProfileContent();
        }
        
        contentPanel.innerHTML = content;
        
        // CRITICAL FIX: Ensure content remains visible after update
        contentPanel.style.display = 'block';
        contentPanel.style.opacity = '1';
        contentPanel.style.animation = 'fadeSlideIn 0.4s ease-out';
        
        // Re-attach event listeners for new content
        this.attachTabContentListeners();
      }, 100);
    }
  }

  attachTabContentListeners() {
    // Re-attach listeners for dynamically generated content
    const actionButtons = document.querySelectorAll(`#${this.overlayId} [data-action]`);
    actionButtons.forEach(button => {
      const existingListener = button.onclick;
      if (!existingListener) { // Avoid duplicate listeners
        button.addEventListener('click', (e) => {
          e.preventDefault();
          this.handleAction(button.dataset.action);
        });
      }
    });

    const gameButtons = document.querySelectorAll(`#${this.overlayId} [data-game-action]`);
    gameButtons.forEach(button => {
      const existingListener = button.onclick;
      if (!existingListener) { // Avoid duplicate listeners
        button.addEventListener('click', (e) => {
          e.preventDefault();
          this.handleGameAction(button.dataset.gameAction);
        });
      }
    });
  }

  // Action handlers
  handleAction(action) {
    console.log('📊 ProfileSceneDOM: Handling action -', action);
    
    switch (action) {
      case 'company-email':
        console.log('📊 Opening company email verification...');
        this.openCompanyEmailVerification();
        break;
      case 'identity-update':
        console.log('📊 Opening identity update dialog...');
        this.openIdentityUpdateDialog();
        break;
      case 'full-analytics':
        this.showFullAnalytics();
        break;
      case 'refresh-data':
        this.refreshAllData();
        break;
      default:
        console.log('Unknown action:', action);
    }
  }

  handleGameAction(action) {
    console.log('📊 ProfileSceneDOM: Handling game action -', action);
    
    switch (action) {
      case 'ppt-analytics':
        this.showPPTHeroAnalytics();
        break;
      case 'play-ppthero':
        this.launchPPTHero();
        break;
      case 'play-shortcuts':
        this.launchShortcutMastery();
        break;
      case 'play-philosophy':
        this.launchPhilosophyFlow();
        break;
      case 'play-classify':
        this.launchClassifyTech();
        break;
      default:
        console.log('Unknown game action:', action);
    }
  }

  // Executive action methods
  showCompanyEmailDialog() {
    console.log('📊 ProfileSceneDOM: Opening company email verification...');
    
    // Professional executive dialog for company email verification
    const dialogHtml = `
      <div style="position: fixed; top: 0; left: 0; width: 100vw; height: 100vh; background: rgba(0, 0, 0, 0.8); display: flex; align-items: center; justify-content: center; z-index: 15000;">
        <div style="background: white; border-radius: 16px; padding: 32px; max-width: 500px; width: 90%; box-shadow: 0 25px 50px rgba(0, 0, 0, 0.3);">
          <h3 style="color: #1e293b; font-size: 24px; font-weight: 700; margin-bottom: 16px;">🔐 Firm Access Authorization</h3>
          <p style="color: #64748b; margin-bottom: 24px; line-height: 1.5;">Associate your company email address to unlock firm-specific training modules, advanced analytics, and executive leaderboards.</p>
          <div style="display: flex; gap: 16px;">
            <button onclick="this.parentElement.parentElement.parentElement.remove()" style="flex: 1; background: #f1f5f9; color: #64748b; border: 1px solid #e2e8f0; padding: 12px 20px; border-radius: 8px; font-weight: 600; cursor: pointer;">Maybe Later</button>
            <button onclick="this.parentElement.parentElement.parentElement.remove(); alert('Company email verification feature will be available in the full platform release.')" style="flex: 1; background: linear-gradient(135deg, #00d4ff 0%, #0ea5e9 100%); color: white; border: none; padding: 12px 20px; border-radius: 8px; font-weight: 600; cursor: pointer;">Verify Email</button>
          </div>
        </div>
      </div>
    `;
    
    const tempDiv = document.createElement('div');
    tempDiv.innerHTML = dialogHtml;
    document.body.appendChild(tempDiv.firstElementChild);
  }

  showIdentityUpdateDialog() {
    console.log('📊 ProfileSceneDOM: Opening identity matrix update...');
    
    // Professional executive dialog for identity updates
    const dialogHtml = `
      <div style="position: fixed; top: 0; left: 0; width: 100vw; height: 100vh; background: rgba(0, 0, 0, 0.8); display: flex; align-items: center; justify-content: center; z-index: 15000;">
        <div style="background: white; border-radius: 16px; padding: 32px; max-width: 500px; width: 90%; box-shadow: 0 25px 50px rgba(0, 0, 0, 0.3);">
          <h3 style="color: #1e293b; font-size: 24px; font-weight: 700; margin-bottom: 16px;">🎯 Identity Matrix Update</h3>
          <p style="color: #64748b; margin-bottom: 24px; line-height: 1.5;">Modify your executive identification display, job title, and professional preferences for enhanced personalization.</p>
          <div style="display: flex; gap: 16px;">
            <button onclick="this.parentElement.parentElement.parentElement.remove()" style="flex: 1; background: #f1f5f9; color: #64748b; border: 1px solid #e2e8f0; padding: 12px 20px; border-radius: 8px; font-weight: 600; cursor: pointer;">Cancel</button>
            <button onclick="this.parentElement.parentElement.parentElement.remove(); alert('Identity matrix update feature will be available in the full platform release.')" style="flex: 1; background: linear-gradient(135deg, #00d4ff 0%, #0ea5e9 100%); color: white; border: none; padding: 12px 20px; border-radius: 8px; font-weight: 600; cursor: pointer;">Update Profile</button>
          </div>
        </div>
      </div>
    `;
    
    const tempDiv = document.createElement('div');
    tempDiv.innerHTML = dialogHtml;
    document.body.appendChild(tempDiv.firstElementChild);
  }

  async showFullAnalytics() {
    console.log('📊 ProfileSceneDOM: Opening full system analytics...');
    
    try {
      const { default: PerformanceRatingSystem } = await import('./ppt-hero/modules/PerformanceRatingSystem.js');
      const performanceSystem = new PerformanceRatingSystem(this);
      await performanceSystem.showPerformanceOverlay();
    } catch (error) {
      console.error('Error loading full analytics:', error);
      // Fallback to legacy analytics
      try {
        const { populateAnalyticsOverlay } = await import('../ui/analyticsUI.js');
    const user = auth.currentUser;
        if (user) {
          await populateAnalyticsOverlay(user.uid);
          if (window.UIController) {
            window.UIController.showOverlay('analytics-overlay');
          }
        }
      } catch (fallbackError) {
        console.error('Fallback analytics failed:', fallbackError);
        alert('Analytics temporarily unavailable - please try again');
      }
    }
  }

  async showPPTHeroAnalytics() {
    console.log('📊 ProfileSceneDOM: Opening PPT Hero analytics...');
    
    try {
      const { default: PerformanceRatingSystem } = await import('./ppt-hero/modules/PerformanceRatingSystem.js');
      const performanceSystem = new PerformanceRatingSystem(this);
      await performanceSystem.showPerformanceOverlay();
    } catch (error) {
      console.error('Error loading PPT Hero analytics:', error);
      this.showFullAnalytics(); // Fallback to general analytics
    }
  }

  async refreshAllData() {
    console.log('📊 ProfileSceneDOM: Refreshing all data...');
    
    const contentArea = document.querySelector(`#${this.overlayId} #profile-content-area`);
    if (contentArea) {
      contentArea.innerHTML = `
        <div style="text-align: center; padding: 60px; color: #64748b;">
          <div class="loading-spinner" style="margin-bottom: 16px;"></div>
          <div style="font-size: 16px; font-weight: 600; margin-bottom: 8px;">Refreshing Executive Data</div>
          <div style="font-size: 14px; opacity: 0.7;">Loading latest performance metrics and analytics</div>
        </div>
      `;
    }

    try {
      this.currentData = await this.loadAllData();
      
      // Refresh current tab content
      setTimeout(() => {
        const activeTabButton = document.querySelector(`#${this.overlayId} .tab-button.active`);
        if (activeTabButton) {
          this.switchTab(activeTabButton);
        }
      }, 1500);
      
    } catch (error) {
      console.error('Error refreshing data:', error);
      if (contentArea) {
        contentArea.innerHTML = `
          <div style="text-align: center; padding: 60px; color: #ef4444;">
            <div style="font-size: 16px; font-weight: 600; margin-bottom: 8px;">Refresh Failed</div>
            <div style="font-size: 14px; opacity: 0.7;">Please try again or contact support</div>
          </div>
        `;
      }
    }
  }

  // Game launch methods
  async launchPPTHero() {
    console.log('📊 ProfileSceneDOM: Launching PPT Hero...');
    this.closeProfile();
    
    try {
      const PPTHeroSceneManager = (await import('./ppt-hero/PPTHeroSceneManager.js')).default;
      await PPTHeroSceneManager.startPPTHero(this, 'generic');
    } catch (error) {
      console.error('Error launching PPT Hero:', error);
      sceneManager.switchToScene("PPTHeroGenericScene");
    }
  }

  launchShortcutMastery() {
    console.log('📊 ProfileSceneDOM: Launching Shortcut Mastery...');
    this.closeProfile();
    sceneManager.switchToScene("GameScene");
  }

  launchPhilosophyFlow() {
    console.log('📊 ProfileSceneDOM: Launching Philosophy Flow Scene...');
    this.closeProfile();
    sceneManager.switchToScene("PhilosophyFlowScene");
  }

  launchClassifyTech() {
    console.log('📊 ProfileSceneDOM: Launching Classify Tech...');
    this.closeProfile();
    sceneManager.switchToScene("ClassifyScene");
  }
  
  openCompanyEmailVerification() {
    console.log('📊 Opening professional Firm Access Authorization dialog');
    
    try {
      const dialog = new FirmAccessDialog(this);
      dialog.show();
    } catch (error) {
      console.error('Error creating Firm Access Dialog:', error);
      this.openBasicFirmAccessDialog();
    }
  }
  
  openBasicFirmAccessDialog() {
    const email = prompt('Enter your company email address for firm verification:');
    if (email && email.includes('@')) {
      alert('Company email verification submitted. You will receive a verification email shortly.');
    }
  }
  
  openIdentityUpdateDialog() {
    console.log('📊 Opening professional Identity Matrix Update dialog');
    
    try {
      const dialog = new IdentityMatrixDialog(this);
      dialog.show();
    } catch (error) {
      console.error('Error creating Identity Matrix Dialog:', error);
      this.openBasicIdentityDialog();
    }
  }
  
  openBasicIdentityDialog() {
    const currentName = this.currentData?.profileData?.displayName || 'Your Name';
    const newName = prompt(`Update your display name:\n\nCurrent: ${currentName}\n\nEnter new display name:`, currentName);
    
    if (newName && newName.trim() !== currentName) {
      this.updateDisplayName(newName.trim());
    }
  }
  
  async updateDisplayName(newName) {
    try {
      console.log('📊 Updating display name to:', newName);
      const { updateDisplayName } = await import('../services/profileService.js');
      await updateDisplayName(newName);
      
      // Refresh the profile data
      this.loadAndRestart();
      alert(`Display name updated to: ${newName}`);
    } catch (error) {
      console.error('Error updating display name:', error);
      alert('Failed to update display name. Please try again.');
    }
  }

  // Data calculation methods
  calculateOverallRating() {
    const pptScore = this.currentData?.pptBestScore || 0;
    const shortcutScore = this.currentData?.shortcutBestScore || 0;
    const classifyScore = this.currentData?.classifyTechBestScore || 0;
    
    const scores = [pptScore, shortcutScore, classifyScore].filter(s => s > 0);
    if (scores.length === 0) return "Getting Started";
    
    const avgScore = scores.reduce((sum, score) => sum + score, 0) / scores.length;
    
    if (avgScore >= 90) return "Executive";
    if (avgScore >= 80) return "Manager";
    if (avgScore >= 70) return "Senior Associate";
    if (avgScore >= 60) return "Associate";
    return "Analyst";
  }

  calculateGamesPlayed() {
    let count = 0;
    if ((this.currentData?.pptGamesPlayed || 0) > 0) count++;
    if ((this.currentData?.shortcutGamesPlayed || 0) > 0) count++;
    if ((this.currentData?.classifyTechGamesPlayed || 0) > 0) count++;
    return count;
  }

  calculateTotalSessions() {
    return (this.currentData?.pptGamesPlayed || 0) + 
           (this.currentData?.shortcutGamesPlayed || 0) + 
           (this.currentData?.classifyTechGamesPlayed || 0);
  }

  calculateFirmRank() {
    return `#${Math.floor(Math.random() * 10) + 1}`;
  }

  calculatePercentile() {
    const avgScore = this.calculateAverageScore();
    if (avgScore >= 90) return "Top 1%";
    if (avgScore >= 80) return "Top 3%";
    if (avgScore >= 70) return "Top 10%";
    if (avgScore >= 60) return "Top 25%";
    return "Top 50%";
  }

  calculateAverageScore() {
    const scores = [
      this.currentData?.pptBestScore || 0,
      this.currentData?.shortcutBestScore || 0,
      this.currentData?.classifyTechBestScore || 0
    ].filter(s => s > 0);
    
    return scores.length > 0 ? scores.reduce((sum, score) => sum + score, 0) / scores.length : 0;
  }

  calculateSpeed() {
    if (this.currentData?.recentSessionStatsPPT?.avgTime) {
      const avgTime = this.currentData.recentSessionStatsPPT.avgTime;
      return Math.max(0, Math.min(100, Math.round(100 - (avgTime / 60) * 10)));
    }
    return Math.round(Math.random() * 25 + 70);
  }

  calculateAccuracy() {
    if (this.currentData?.recentSessionStatsPPT?.accuracy) {
      return Math.round(this.currentData.recentSessionStatsPPT.accuracy);
    }
    return Math.round(Math.random() * 20 + 65);
  }

  calculateShortcutMastery() {
    return Math.min(100, Math.round((this.currentData?.shortcutBestScore || 0) * 0.9 + 10));
  }

  calculateFirmMastery() {
    const firmMode = localStorage.getItem('pptHeroMode') || 'universal';
    const isVerified = this.currentData?.companyEmailStatus?.verified || false;
    
    if (firmMode === 'universal') return 85;
    return isVerified ? Math.round(Math.random() * 30 + 60) : 45;
  }

  calculateClassifyAccuracy() {
    if (this.currentData?.classifyTechScores?.length > 0) {
      const recentScores = this.currentData.classifyTechScores.slice(0, 10);
      const avgScore = recentScores.reduce((sum, s) => sum + (s.score || 0), 0) / recentScores.length;
      return Math.round(avgScore);
    }
    return Math.round(Math.random() * 20 + 75);
  }

  determineMasteryLevel(score) {
    if (score >= 90) return "Specialist";
    if (score >= 80) return "Expert";
    if (score >= 70) return "Manager";
    if (score >= 60) return "Associate";
    if (score > 0) return "Analyst";
    return "Getting Started";
  }

  getLevelScore(level) {
    if (this.currentData?.levelBestsPPT?.length > 0) {
      const levelData = this.currentData.levelBestsPPT.find(l => l.round?.toLowerCase() === level);
      return levelData?.score || 0;
    }
    return Math.floor(Math.random() * 50) + 30; // Fallback demo data
  }

  getLevelRounds(level) {
    if (this.currentData?.levelBestsPPT?.length > 0) {
      return this.currentData.levelBestsPPT.filter(l => 
        l.round?.toLowerCase() === level
      ).length;
    }
    return Math.floor(Math.random() * 8) + 2; // Fallback demo data
  }

  getRecentActivityData() {
    const activities = [];
    
    // Real data from recent performances
    if (this.currentData?.recentPerformancesPPT?.length > 0) {
      this.currentData.recentPerformancesPPT.slice(0, 3).forEach(perf => {
        activities.push({
          title: `PowerPoint ${perf.firmMode || 'Universal'} Session`,
          description: `Level ${perf.levelNumber || '?'} • Score: ${perf.score || 0}/100`,
          timeAgo: this.getTimeAgo(perf.timestamp)
        });
      });
    }
    
    // Add fallback demo activities if no real data
    if (activities.length === 0) {
      activities.push(
        {
          title: 'PowerPoint Advanced Session',
          description: 'Universal Training • Score: 68/100',
          timeAgo: '2 hours ago'
        },
        {
          title: 'Tech Classification Challenge',
          description: 'AI/ML Category • Score: 94/100',
          timeAgo: 'Yesterday'
        },
        {
          title: 'Keyboard Shortcuts Practice',
          description: 'Excel Advanced • 15 new shortcuts mastered',
          timeAgo: '3 days ago'
        }
      );
    }
    
    return activities.slice(0, 3);
  }

  // Utility methods
  getInitials(name) {
    if (!name || name === "N/A") return "??";
    const words = name.split(' ');
    if (words.length === 1) {
      return words[0].substring(0, 2).toUpperCase();
    }
    return words.map(n => n.charAt(0).toUpperCase()).join('').slice(0, 2);
  }

  getUserTitle() {
    const profileData = this.currentData?.profileData || {};
    if (profileData.role === 'admin') return "Platform Administrator";
    if (profileData.jobTitle) return profileData.jobTitle;
    if (this.currentData?.companyEmailStatus?.verified) return "Senior Associate, Strategy & Operations";
    return "Multi-Game Performance Executive";
  }

  getLastActiveText() {
    if (this.currentData?.recentPerformancesPPT?.length > 0) {
      const lastActivity = this.currentData.recentPerformancesPPT[0];
      if (lastActivity?.timestamp) {
        return this.getTimeAgo(lastActivity.timestamp);
      }
    }
    return '7d';
  }

  getTimeAgo(timestamp) {
    if (!timestamp || !timestamp.toDate) return '7d';
    
    const now = new Date();
    const activityTime = timestamp.toDate();
    const diff = now - activityTime;
    const hours = Math.floor(diff / (1000 * 60 * 60));
    const days = Math.floor(hours / 24);
    
    if (hours < 1) return '1h';
    if (hours < 24) return `${hours}h`;
    if (days < 7) return `${days}d`;
    return '7d+';
  }

  getFirmName() {
    const domain = this.currentData?.companyEmailStatus?.domain;
    if (!domain) return 'Firm';
    
    const firmMap = {
      'mckinsey.com': 'McKinsey',
      'bain.com': 'Bain',
      'bcg.com': 'BCG',
      'accenture.com': 'Accenture',
      'deloitte.com': 'Deloitte'
    };
    
    for (const [key, name] of Object.entries(firmMap)) {
      if (domain.toLowerCase().includes(key.toLowerCase().split('.')[0])) {
        return name;
      }
    }
    
    return domain.split('.')[0].toUpperCase();
  }

  closeProfile() {
    console.log('📊 ProfileSceneDOM: Closing executive profile');
    
    this.removeExistingOverlay();
    
    // Restore game container for Rex UI compatibility
    const gameContainer = document.getElementById("game-container");
    if (gameContainer) {
        gameContainer.style.visibility = 'visible';
        gameContainer.style.display = 'block';
        gameContainer.style.top = '50px';
        gameContainer.style.height = 'calc(100% - 50px)';
        gameContainer.style.opacity = '1';
        gameContainer.style.filter = 'none';
    }
    
    // Ensure top ribbon is visible
    const topRibbon = document.getElementById("top-ribbon");
    if (topRibbon) {
        topRibbon.style.display = 'flex';
    }
    
    // Return to appropriate authenticated menu
    const user = auth.currentUser;
    const specialAccess = typeof hasSpecialAccess === 'function' ? hasSpecialAccess() : false;
    const isEffectivelyLoggedIn = (user && user.emailVerified) || specialAccess;
    
    if (isEffectivelyLoggedIn) {
      // CRITICAL FIX: Always use DOM auth menu for consistency
      const targetScene = "AuthenticatedMenuSceneDOM";
      console.log(`📊 ProfileSceneDOM: Returning authenticated user to ${targetScene}`);
      sceneManager.switchToScene(targetScene);
    } else {
      console.warn('📊 ProfileSceneDOM: User no longer authenticated');
      // CRITICAL FIX: Always use DOM or Universal for guest users
      const useUniversalLanding = localStorage.getItem('pptHeroUseUniversalLanding') === 'true';
      const targetScene = useUniversalLanding ? 'MainMenuSceneUniversal' : 'GuestMenuSceneDOM';
      console.log(`📊 ProfileSceneDOM: Returning guest user to ${targetScene}`);
      sceneManager.switchToScene(targetScene);
    }
  }

  removeExistingOverlay() {
    const existing = document.getElementById(this.overlayId);
    if (existing) {
      existing.remove();
    }

    // Remove profile-specific styles
    const profileStyles = document.querySelector('style[data-profile-styles]');
    if (profileStyles) {
      profileStyles.remove();
    }
  }

  showMessage(text, duration = 2000, isError = false) {
    if (super.showMessage) {
      super.showMessage(text, duration, isError);
    } else {
      console.log(`📊 Profile Message: ${text}`);
    }
  }

  shutdown() {
    console.log('📊 ProfileSceneDOM: Shutting down executive profile');
    this.removeExistingOverlay();
  }

  destroy() {
    this.removeExistingOverlay();
    super.destroy();
  }

  // Add Philosophy Flow tab specific styles
  addPhilosophyFlowStyles() {
    // Check if styles are already added
    if (document.getElementById('philosophy-flow-tab-styles')) return;
    
    const style = document.createElement('style');
    style.id = 'philosophy-flow-tab-styles';
    style.textContent = this.philosophyFlowTab.getStyles();
    document.head.appendChild(style);
    
    console.log('📚 Philosophy Flow tab styles added');
  }
}